import React, { useState, useEffect } from "react";
import { useStripe, useElements, IbanElement } from "@stripe/react-stripe-js";
import { useForm, Controller } from "react-hook-form";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { 
  FaArrowLeft, 
  FaCrown,
  FaCheck,
  FaUniversity
} from "react-icons/fa";
import { Helmet } from "react-helmet";
import { format } from "date-fns";
import LogoBlue from "../../common/assets/fideliz-logo-blue.png";

const PricingPlans = () => {
  const { control, handleSubmit, watch, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);
  const [ibanValid, setIbanValid] = useState(false);
  const { currentUser, setCurrentUser, updateUser } = useAuth();
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const authToken = localStorage.getItem("token");

  const isSubscribed = currentUser?.stripe_subscription_id && currentUser?.plan_level === 3;
  const nextMonthDate = format(new Date(new Date().getFullYear(), new Date().getMonth() + 2, 1), "dd/MM/yyyy");

  // Redirection si déjà abonné
  useEffect(() => {
    if (isSubscribed) {
      navigate('/optician-dashboard');
    }
  }, [isSubscribed, navigate]);

  const handleSubscribe = async (data) => {
    if (!stripe || !elements) {
      alert("Stripe n'est pas encore chargé. Veuillez réessayer.");
      return;
    }

    setLoading(true);

    try {
      // 1. Créer un SetupIntent
      const clientSecretResponse = await api.post(
        "/subscriptions/intent",
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      const clientSecret = clientSecretResponse.data.client_secret;
      const ibanElement = elements.getElement(IbanElement);

      if (!ibanElement) {
        alert("Erreur: champ IBAN non disponible");
        return;
      }

      // 2. Confirmer le SEPA avec Stripe
      const { error, setupIntent } = await stripe.confirmSepaDebitSetup(clientSecret, {
        payment_method: {
          sepa_debit: ibanElement,
          billing_details: {
            name: data.name,
            email: currentUser.email,
          },
        },
        return_url: `${window.location.origin}/optician-dashboard`,
      });

      if (error) {
        alert(`Erreur: ${error.message}`);
        return;
      }

      if (setupIntent.status === "requires_action" && setupIntent.next_action) {
        window.location.href = setupIntent.next_action.redirect_to_url.url;
        return;
      }

      const paymentMethodId = setupIntent.payment_method;

      // 3. Créer l'abonnement
      const response = await api.post(
        "/users/subscription",
        {
          planId: 3, // Plan Premium
          paymentMethodType: "iban",
          iban: paymentMethodId,
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.status === 200) {
        setCurrentUser(response.data.data.user);
        alert('✅ Abonnement Premium activé avec succès !');
        navigate('/optician-dashboard');
      }
    } catch (error) {
      console.error("Erreur:", error);
      alert(error.response?.data?.message || "Une erreur est survenue lors de la souscription");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('⚠️ Êtes-vous sûr de vouloir résilier votre abonnement Fideliz ?')) {
      return;
    }

    setLoading(true);
    try {
      await api.post('/subscription/cancel', {}, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      alert('✅ Votre abonnement a été résilié avec succès.');
      await updateUser();
      navigate('/optician-dashboard');
    } catch (error) {
      alert('❌ Erreur lors de la résiliation. Contactez contact@fideliz.fr');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const ibanElementOptions = {
    supportedCountries: ['SEPA'],
    style: {
      base: {
        color: "#1f2937",
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSmoothing: "antialiased",
        fontSize: "16px",
        "::placeholder": {
          color: "#9ca3af",
        },
      },
      invalid: {
        color: "#ef4444",
        iconColor: "#ef4444",
      },
    },
  };

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      <Helmet>
        <title>Abonnement Premium | Fideliz</title>
      </Helmet>

      {/* Header simple */}
      <header className="bg-gray-50 px-4 sm:px-6 md:px-8 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <img src={LogoBlue} alt="Fideliz" className="h-6 sm:h-7" />
          {currentUser?.optician_name && (
            <div className="flex flex-col items-end gap-2">
              <h2 className="text-xs sm:text-sm font-semibold text-gray-600 truncate max-w-xs">
                {currentUser.optician_name}
              </h2>
              {currentUser?.logo && (
                <img 
                  src={currentUser.logo} 
                  alt={currentUser.optician_name} 
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-cover bg-white"
                />
              )}
            </div>
          )}
        </div>
      </header>

      {/* Contenu principal - One page centré, non scrollable */}
      <main className="flex-1 px-4 sm:px-6 md:px-8 py-6 overflow-hidden flex items-center justify-center">
        <div className="max-w-2xl w-full">
          
          {/* Formulaire de souscription */}
          <div className="bg-white rounded-2xl overflow-hidden border-2 border-gray-200">
              
              {/* Header */}
              <div className="px-6 sm:px-8 py-6 sm:py-8 text-center border-b border-gray-200">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaUniversity className="w-7 h-7 text-fideliz-blue" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                  Finaliser votre abonnement
                </h2>
                <p className="text-sm text-gray-600">
                  Renseignez votre IBAN pour activer votre compte
                </p>
              </div>

              {/* Formulaire */}
              <form onSubmit={handleSubmit(handleSubscribe)} className="px-6 sm:px-8 py-6 sm:py-8">
                
                {/* Nom du titulaire */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nom du titulaire
                  </label>
                  <Controller
                    name="name"
                    control={control}
                    defaultValue=""
                    rules={{ required: true }}
                    render={({ field }) => (
                      <input
                        {...field}
                        placeholder="Jean Dupont"
                        className={`w-full px-4 py-3 border-2 rounded-xl text-gray-900 focus:outline-none focus:border-fideliz-blue transition-colors ${
                          errors.name ? 'border-red-500' : 'border-gray-200'
                        }`}
                      />
                    )}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-xs mt-1">Le nom est requis</p>
                  )}
                </div>

                {/* IBAN */}
                <div className="mb-6">
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                    <FaUniversity className="w-4 h-4" />
                    IBAN
                  </label>
                  <div className={`px-4 py-3 border-2 rounded-xl bg-white transition-colors ${
                    ibanValid ? 'border-green-500' : 'border-gray-200'
                  }`}>
                    <IbanElement
                      options={ibanElementOptions}
                      onChange={(event) => setIbanValid(event.complete)}
                    />
                  </div>
                  {ibanValid && (
                    <p className="text-green-600 text-xs mt-1 flex items-center gap-1">
                      <FaCheck className="w-3 h-3" /> IBAN valide
                    </p>
                  )}
                </div>

                {/* Bouton de souscription */}
                <button
                  type="submit"
                  disabled={loading || !watch("name") || !ibanValid}
                  className={`w-full px-6 py-4 rounded-xl font-bold text-base transition-all ${
                    loading || !watch("name") || !ibanValid
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-fideliz-blue to-blue-600 text-white'
                  }`}
                >
                  {loading ? 'Activation en cours...' : 'Activer mon abonnement'}
                </button>

                <p className="text-center text-xs text-gray-500 mt-4">
                  Paiement sécurisé par Stripe • SEPA
                </p>
              </form>
            </div>

        </div>
      </main>
    </div>
  );
};

export default PricingPlans;
